/**
 * The account's default copy (the rule is in `shared.ts`): the header and tab row over the
 * shopper's own pages, Account and the inventory, which Add funds' balance repeats, then the
 * Account page's own sections. The lock notice, the save errors and the Verification panel
 * (identity verification, a compliance panel) are the platform's and stay with the storefront's
 * hooks; only Verification's tab is here.
 */
import { capitalizeTerm, type StorefrontTerminology } from "../storefrontTerminology.js";
import { battleRoundsLabel } from "./battles.js";
import { STOREFRONT_FRAME_COPY } from "./frame.js";
import { browseBoxesLabel, STOREFRONT_PAGING_COPY } from "./shared.js";

/** The tabs over the shopper's own pages: the inventory (its own route), then Account's two. */
export const ACCOUNT_TAB_KEYS = ["vault", "settings", "history"] as const;
export type AccountTabKey = (typeof ACCOUNT_TAB_KEYS)[number];

/** The header and tab row over the shopper's own pages, Account and the inventory. */
export interface AccountHeaderCopy {
  header: {
    /** The heading, and the avatar's initials, while the shopper has chosen no username. */
    unnamed: string;
    /** The line under the name, given the month the account was made ("September 2026"). */
    memberSince: (month: string) => string;
    /** The way to the shopper's public profile, here and on Profile's username row. */
    viewProfile: string;
    /** The balance's label, here and in Add funds. */
    balance: string;
    /** Opens Add funds. */
    deposit: string;
  };
  /** The tab row's accessible name, and each tab. */
  tabs: { label: string; items: Record<AccountTabKey, string> };
}

export function defaultAccountHeaderCopy(terms: StorefrontTerminology): AccountHeaderCopy {
  const { inventory } = terms;
  return {
    header: {
      unnamed: "Account",
      memberSince: (month) => `Member since ${month}`,
      viewProfile: "View profile",
      balance: "Balance",
      deposit: STOREFRONT_FRAME_COPY.deposit,
    },
    tabs: {
      label: "Account",
      items: {
        vault: capitalizeTerm(inventory.singular),
        settings: "Settings",
        history: "History",
      },
    },
  };
}

/** Settings' panels, in the order its tabs list them. */
export const ACCOUNT_SETTINGS_SECTIONS = [
  "profile",
  "shipping",
  "verification",
  "advanced",
] as const;
export type AccountSettingsSection = (typeof ACCOUNT_SETTINGS_SECTIONS)[number];

/** How one of the shopper's battles stands for them in History. */
export type BattleHistoryOutcome = "waiting" | "in-progress" | "won" | "lost";

/** The Account page's own words, by section, in the store's terms. */
export interface AccountCopy {
  /** Settings' tabs: their accessible name, and each panel's tab. */
  settingsNav: { label: string; items: Record<AccountSettingsSection, string> };
  profile: {
    title: string;
    description: string;
    /** The label of each of the account's facts. */
    rows: Record<"username" | "email" | "externalId" | "mode" | "nonce" | "memberSince", string>;
    /** The username's stand-in while none is chosen. */
    noUsername: string;
    /** The username's action: Choose while none is chosen, Change after. */
    choose: string;
    change: string;
    viewProfile: string;
  };
  shipping: {
    title: string;
    description: string;
    noAddress: string;
    /** The address's action: Add while none is saved, Edit after. */
    add: string;
    edit: string;
    /** Said in place of the destinations, with no form, where the store ships nowhere. */
    unavailable: string;
    destinations: {
      title: string;
      columns: { country: string; cost: string; delivery: string };
      /** A destination that costs nothing. */
      free: string;
    };
  };
  advanced: {
    title: string;
    autoClaim: { label: string; description: string };
    /** The sound level the shopper keeps: the group's heading, the slider's name and what it does. */
    audio: { title: string; volumeLabel: string; muteLabel: string; description: string };
    /** Beside a switch, by its state. */
    on: string;
    off: string;
  };
  history: {
    title: string;
    /** The switch between the two lists: its accessible name. */
    viewLabel: string;
    /** Each list's next page, and the count line under it. */
    paging: { loadMore: string; showing: (shown: number) => string };
    opens: {
      label: string;
      columns: { item: string; value: string; cost: string; when: string; verify: string };
      /** Each row's link to its verification. */
      verify: string;
      /** Said with no opens, beside the link to the catalog. */
      empty: string;
      browse: string;
    };
    battles: {
      label: string;
      columns: {
        battle: string;
        result: string;
        pot: string;
        cost: string;
        when: string;
        link: string;
      };
      /** In place of a pot not yet known. */
      pendingPot: string;
      /** Said with no battles, beside the link to the lobby. */
      empty: string;
      start: string;
      /** A row's length and size: "3 rounds · 2 players". */
      summary: (rounds: number, players: number) => string;
      /** How the battle stands for the shopper, and the row's link, by that. */
      outcome: Record<BattleHistoryOutcome, string>;
      link: Record<BattleHistoryOutcome, string>;
    };
    /** The line under the lists: opens alone, or beside the store's battles. */
    footnote: { opensOnly: string; withBattles: string };
  };
}

export function defaultAccountCopy(terms: StorefrontTerminology): AccountCopy {
  const { box, item, battle } = terms;
  return {
    settingsNav: {
      label: "Settings",
      items: {
        profile: "Profile",
        shipping: "Shipping",
        verification: "Verification",
        advanced: "Advanced",
      },
    },
    profile: {
      title: "Profile",
      description: "Other players see your username, never your email.",
      rows: {
        username: "Username",
        email: "Email",
        externalId: "External ID",
        mode: "Mode",
        nonce: "Opens (nonce)",
        memberSince: "Member since",
      },
      noUsername: "Not chosen",
      choose: "Choose",
      change: "Change",
      viewProfile: defaultAccountHeaderCopy(terms).header.viewProfile,
    },
    shipping: {
      title: "Shipping address",
      description: `Where your withdrawn ${item.plural} ship. Each order keeps its own copy, so a change here never moves an order already placed.`,
      noAddress: "No address on file.",
      add: "Add address",
      edit: "Edit address",
      unavailable: "Shipping is currently unavailable.",
      destinations: {
        title: "Where we ship",
        columns: { country: "Country", cost: "Shipping", delivery: "Delivery" },
        free: "Free",
      },
    },
    advanced: {
      title: "Advanced",
      autoClaim: {
        label: "Auto-claim",
        description: `Claim each ${box.singular} reveal and ${battle.singular} result the moment it lands, with no prompt.`,
      },
      audio: {
        title: "Audio",
        volumeLabel: "Volume",
        muteLabel: "Mute sounds",
        description: "How loud the store's sounds play. All the way down mutes them.",
      },
      on: "On",
      off: "Off",
    },
    history: {
      title: "History",
      viewLabel: "Show",
      paging: STOREFRONT_PAGING_COPY,
      opens: {
        label: "Opens",
        columns: {
          item: capitalizeTerm(item.singular),
          value: "Value",
          cost: "Cost",
          when: "When",
          verify: "Verify",
        },
        verify: "Verify",
        empty: "No opens yet.",
        browse: browseBoxesLabel(terms),
      },
      battles: {
        label: capitalizeTerm(battle.plural),
        columns: {
          battle: capitalizeTerm(battle.singular),
          result: "Result",
          pot: "Pot",
          cost: "Cost",
          when: "When",
          link: "Open",
        },
        pendingPot: "—",
        empty: `No ${battle.plural} yet.`,
        start: "Start one",
        summary: (rounds, players) => `${battleRoundsLabel(rounds)} · ${players} players`,
        outcome: {
          waiting: "Waiting for players",
          "in-progress": "In progress",
          won: "Won",
          lost: "Lost",
        },
        link: { waiting: "Lobby", "in-progress": "Watch", won: "Results", lost: "Results" },
      },
      footnote: {
        opensOnly: "Every open links to its verification.",
        withBattles: `Every open links to its verification. In ${battle.plural}, the winner takes every ${item.singular}.`,
      },
    },
  };
}
